/**
 * ProShip Direct — confirm a payment with the code from the registered email.
 *
 * For the payments that have no review step of their own: accepting an offer and paying a renewal
 * that failed. The caller passes what is being paid for (`summary`) and the call that pays
 * (`onPay`, given { otpTokenId, otp }); this modal mails the code on open and pays with it. The
 * server checks the code before it charges, so a wrong one is reported here and charges nothing.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';

import MUIModal from '../../../common/MUIModal';
import ActivationOtpStep from './ActivationOtpStep';
import { primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { GAP } from '../../../styles/foundation/spacing';
import { OTP_LENGTH } from '../data/activationOtp';
import useActivationOtp from '../data/useActivationOtp';

export default function PaymentOtpModal({ title, summary, payLabel, onPay, onClose }) {
  const otp = useActivationOtp();
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);

  // Mail the code as the modal opens: the merchant has already chosen to pay.
  useEffect(() => {
    otp.send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pay = async () => {
    const proof = otp.proof();
    if (!proof) return;
    setPaying(true);
    setPayError(null);
    try {
      await onPay(proof);
      onClose();
    } catch (e) {
      setPayError(e?.message || 'The payment could not be completed.');
    } finally {
      setPaying(false);
    }
  };

  return (
    <MUIModal
      open
      handleClose={() => { if (!paying) onClose(); }}
      maxWidth='sm'
      title={title}
      action={
        <Box sx={{ display: 'flex', gap: GAP.sm }}>
          <Button size='small' sx={secondaryBtnSx} onClick={onClose} disabled={paying}>Cancel</Button>
          <Button
            size='small'
            variant='contained'
            sx={primaryBtnSx}
            disabled={paying || otp.sending || otp.entered.length !== OTP_LENGTH}
            onClick={pay}
            startIcon={paying ? <CircularProgress size={12} /> : null}
          >
            {paying ? 'Charging…' : payLabel}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {summary}
        {payError && <Alert severity='error'>{payError}</Alert>}
        <ActivationOtpStep {...otp.stepProps} />
      </Box>
    </MUIModal>
  );
}
